import { useContext } from 'react';
import { Button, Card, InputNumber, Modal, Space, Table, Tag, Typography } from 'antd';
import {
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    RedoOutlined,
    SyncOutlined,
} from '@ant-design/icons';

import useCityInvoices from './useCityInvoices';
import PageHeader from '../../components/PageHeader';
import ActiveFiltersBar from '../../components/ActiveFiltersBar';
import TableCard from '../../components/TableCard';
import TableDicaHint from '../../components/TableDicaHint';
import TablePagination from '../../components/TablePagination';
import ModalInvoiceDetails from './components/ModalInvoiceDetails';
import { ThemeContext } from '../../contexts/ThemeContext';
import { poeValorComPontosEmMil } from '../../utils/Conversores';

const { Text } = Typography;

export default function CityInvoicesAwaiting() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        columns,
        isLoading,
        isReprocessingBatch,
        tableData,
        totalItems,
        rawTotalCount,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        tableParams,
        activeFilterItems,
        handleConfirmReprocessAllInvoices,
        isModalConfirmReprocessOpen,
        handleCancelReprocessAllInvoices,
        handleReprocessAll,
        showModalInvoice,
        handleShowModalInvoice,
        invoiceSelected,
        setInvoiceSelected,
        daysWithoutAttempt,
        setDaysWithoutAttempt,
        overdueInvoices,
        handleReprocessOverdue,
        isAdmin,
    } = useCityInvoices();

    const cardBg = isDark ? '#141414' : '#ffffff';
    const cardBorder = isDark ? '#303030' : '#e2e8f0';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <PageHeader
                icon={<ClockCircleOutlined />}
                title="Notas Fiscais em Contingência"
                tag={<Tag color="orange">{poeValorComPontosEmMil(rawTotalCount || 0)} em contingência</Tag>}
                subtitle="Notas fiscais aguardando processamento pelo cron (executado nos dias pares, até 3 tentativas antes de erro)."
            />

            {isAdmin && rawTotalCount > 0 && (
                <Card
                    style={{
                        background: cardBg,
                        borderColor: cardBorder,
                        borderRadius: 10,
                    }}
                    bodyStyle={{
                        padding: '12px 18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 12,
                    }}
                >
                    <Space wrap align="center" size={10}>
                        <Text style={{ fontSize: 13, color: isDark ? '#d1d5db' : '#475569', fontWeight: 500 }}>
                            Sem tentativa há mais de
                        </Text>
                        <InputNumber
                            min={1}
                            max={365}
                            value={daysWithoutAttempt}
                            onChange={(v) => setDaysWithoutAttempt(v ?? 2)}
                            style={{ width: 70, borderRadius: 6 }}
                        />
                        <Text style={{ fontSize: 13, color: isDark ? '#d1d5db' : '#475569', fontWeight: 500 }}>
                            dias
                        </Text>
                        <Button
                            icon={<SyncOutlined spin={isReprocessingBatch} />}
                            onClick={handleReprocessOverdue}
                            loading={isReprocessingBatch}
                            disabled={isLoading || overdueInvoices.length === 0}
                            style={{
                                borderRadius: 6,
                                fontWeight: 500,
                            }}
                        >
                            Reprocessar atrasadas ({overdueInvoices.length})
                        </Button>
                    </Space>

                    <Button
                        type="primary"
                        icon={<RedoOutlined spin={isReprocessingBatch} />}
                        onClick={handleConfirmReprocessAllInvoices}
                        loading={isReprocessingBatch}
                        disabled={isLoading}
                        style={{
                            backgroundColor: '#fa8c16',
                            borderColor: '#fa8c16',
                            borderRadius: 6,
                            fontWeight: 600,
                        }}
                    >
                        Reprocessar todas ({rawTotalCount})
                    </Button>
                </Card>
            )}

            <ActiveFiltersBar
                filters={activeFilterItems}
                onClearAll={handleClearAllFilters}
            />

            <TableDicaHint text="Dê um clique na linha ou utilize o botão de ação para ver os detalhes da nota fiscal em contingência." />

            <TablePagination
                current={tableParams.pagination.current}
                pageSize={tableParams.pagination.pageSize}
                total={totalItems}
                itemName="notas em contingência"
                pageSizeOptions={['10', '25', '50', '100']}
                onChange={handlePaginationChange}
            />

            <TableCard>
                <Table
                    rowKey="id"
                    className={`modern-data-table ${isDark ? 'modern-data-table-dark' : ''}`}
                    columns={columns}
                    dataSource={tableData}
                    loading={isLoading}
                    pagination={false}
                    scroll={{ x: 950 }}
                    onChange={handleTableChange}
                    onRow={(record) => ({
                        onClick: () => {
                            setInvoiceSelected(record);
                            handleShowModalInvoice();
                        },
                        style: {
                            cursor: 'pointer',
                        },
                    })}
                />
            </TableCard>

            <TablePagination
                current={tableParams.pagination.current}
                pageSize={tableParams.pagination.pageSize}
                total={totalItems}
                itemName="notas em contingência"
                pageSizeOptions={['10', '25', '50', '100']}
                onChange={handlePaginationChange}
            />
            <Modal
                title={
                    <Space align="center" size={8}>
                        <ExclamationCircleOutlined style={{ color: '#fa8c16', fontSize: 20 }} />
                        <span>Confirmar Reprocessamento em Lote</span>
                    </Space>
                }
                open={isModalConfirmReprocessOpen}
                onOk={handleReprocessAll}
                okText="Reprocessar Todas"
                cancelText="Cancelar"
                okButtonProps={{
                    style: { backgroundColor: '#fa8c16', borderColor: '#fa8c16' },
                    loading: isReprocessingBatch,
                }}
                cancelButtonProps={{
                    disabled: isReprocessingBatch,
                }}
                onCancel={handleCancelReprocessAllInvoices}
                centered
            >
                <p style={{ margin: '12px 0 0 0', fontSize: 14, color: isDark ? '#d1d5db' : '#4b5563' }}>
                    Deseja realmente enviar todas as <strong>{rawTotalCount}</strong> notas em contingência para reprocessamento imediato?
                </p>
            </Modal>

            <ModalInvoiceDetails
                open={showModalInvoice}
                onClose={handleShowModalInvoice}
                invoice={invoiceSelected}
            />
        </div>
    );
}
